import { useCallback, useEffect, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  AppstoreOutlined,
  ExperimentOutlined,
  LeftOutlined,
  PlusOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

import { reefTanks } from "api/reef";
import { fetchSalifertTargets, fetchSalifertTests } from "api/salifert";
import {
  resolveSelectedTankSelectValue,
  useSelectedTank,
  useValidateSelectedTank,
} from "contexts/SelectedTankContext";
import SalifertTrendsDataMobileTable from "components/salifert/trends/SalifertTrendsDataMobileTable";
import SalifertTrendsMuiDashboard from "components/salifert/trends/SalifertTrendsMuiDashboard";
import KeepMountedTabPanel, {
  KeepMountedTabPanels,
} from "components/common/KeepMountedTabPanel";
import PageLoadingBar from "components/common/PageLoadingBar";
import PanelLoadingState from "components/common/PanelLoadingState";
import useBlockingPageLoad from "hooks/useBlockingPageLoad";
import SalifertTestPageShell from "components/salifert/SalifertTestPageShell";
import { SALIFERT_TEST_DEFINITIONS } from "data/salifertTestDefinitions";
import useZebraRowColors from "hooks/useZebraRowColors";
import { parseAppDateTime } from "utils/dosingFormat";
import { useThemeBorders } from "utils/themeBorder";
import { withAlpha } from "utils/colorUtils";
import { scrollRegionNestedSx } from "utils/scrollRegion";

const NO_TANK = "";
const TRENDS_VIEW_SESSION_KEY = "salifert-trends-view-mode";
const LEGEND_SYMBOLS = {
  kh_alk: "dKH",
  calcium: "Ca",
  magnesium: "Mg",
  nitrate: "NO3",
  nitrite: "NO2",
  ammonia: "NH3",
  phosphate: "PO4",
  salinity: "Sal",
  ph: "pH",
  iodine: "I",
  copper: "Cu",
};
const STATUS_BANDS = {
  kh_alk: { warn: 0.5, alert: 1.0 },
  calcium: { warn: 20, alert: 40 },
  magnesium: { warn: 50, alert: 100 },
  nitrate: { warn: 2, alert: 5 },
  nitrite: { warn: 0.05, alert: 0.1 },
  ammonia: { warn: 0.05, alert: 0.1 },
  phosphate: { warn: 0.02, alert: 0.05 },
  salinity: { warn: 0.5, alert: 1.0 },
  ph: { warn: 0.1, alert: 0.2 },
  iodine: { warn: 0.01, alert: 0.03 },
  copper: { warn: 0.03, alert: 0.08 },
};
const GOLDEN_STANDARD_VALUES = {
  kh_alk: 8.0,
  calcium: 420,
  magnesium: 1350,
  nitrate: 5,
  nitrite: 0,
  ammonia: 0,
  phosphate: 0.03,
  salinity: 35,
  ph: 8.3,
  iodine: 0.06,
  copper: 0,
};

function formatDateOnly(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString();
}

function todayDateInputValue() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export default function SalifertTrendsPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { tableSx } = useZebraRowColors();
  const { strongBorderColor, tableFrameSx, panelBorderColor } = useThemeBorders("strong");
  const downMD = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const [range, setRange] = useState("month");
  const [scaleMode, setScaleMode] = useState("relative");
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window === "undefined") return "data";
    try {
      const saved = window.sessionStorage.getItem(TRENDS_VIEW_SESSION_KEY);
      if (saved === "graph" || saved === "data") return saved;
      return "data";
    } catch {
      return "data";
    }
  });
  const [dataAsOfDate, setDataAsOfDate] = useState(todayDateInputValue);
  const [hiddenCodes, setHiddenCodes] = useState([]);
  const [tanks, setTanks] = useState([]);
  const [tanksLoaded, setTanksLoaded] = useState(false);
  const { selectedTankId, setSelectedTankId } = useSelectedTank();
  const tankId = selectedTankId;
  const tankSelectValue = resolveSelectedTankSelectValue(tankId, tanks);
  useValidateSelectedTank(tanks, tanksLoaded);
  const [rows, setRows] = useState([]);
  const [allTargets, setAllTargets] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);

  const trendsDataHeaderCellSx = useMemo(
    () => ({
      whiteSpace: "nowrap",
    }),
    [],
  );

  const loadRows = useCallback(async () => {
    setDataLoading(true);
    try {
      const json = await fetchSalifertTests({ limit: 500 });
      setRows(Array.isArray(json?.data) ? json.data : []);
    } catch {
      setRows([]);
    } finally {
      setDataLoading(false);
    }
  }, []);
  const loadAllTargets = useCallback(async () => {
    try {
      const json = await fetchSalifertTargets({});
      setAllTargets(Array.isArray(json?.data) ? json.data : []);
    } catch {
      setAllTargets([]);
    }
  }, []);

  useEffect(() => {
    reefTanks()
      .then((json) => setTanks(Array.isArray(json?.data) ? json.data : []))
      .catch(() => setTanks([]))
      .finally(() => setTanksLoaded(true));
  }, []);
  useEffect(() => {
    loadRows().catch(() => setRows([]));
  }, [loadRows]);
  useEffect(() => {
    loadAllTargets().catch(() => setAllTargets([]));
  }, [loadAllTargets]);
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.sessionStorage.setItem(TRENDS_VIEW_SESSION_KEY, viewMode);
    } catch {
      // ignore storage issues
    }
  }, [viewMode]);

  const filteredRows = useMemo(() => {
    return rows
      .filter((row) => (tankId ? row.tank_id === tankId : false))
      .filter((row) => row.primary_value != null);
  }, [rows, tankId]);

  const byTest = useMemo(() => {
    const map = new Map();
    SALIFERT_TEST_DEFINITIONS.forEach((def) =>
      map.set(def.code, { def, data: [] }),
    );
    filteredRows.forEach((row) => {
      if (!map.has(row.test_code)) return;
      const ts = parseAppDateTime(row.recorded_at)?.getTime() ?? NaN;
      const value = Number(row.primary_value);
      if (!Number.isFinite(ts) || !Number.isFinite(value)) return;
      map.get(row.test_code).data.push({ date: ts, value });
    });
    return Array.from(map.values()).map((entry) => ({
      ...entry,
      data: entry.data.sort((a, b) => a.date - b.date),
    }));
  }, [filteredRows]);

  const byTestWithData = useMemo(
    () => byTest.filter((entry) => entry.data.length > 0),
    [byTest],
  );

  const targetByCode = useMemo(() => {
    const map = new Map();
    if (!tankId) return map;
    allTargets.forEach((row) => {
      if (row?.tank_id !== tankId) return;
      const value = Number(row?.target_value);
      if (row?.test_code && Number.isFinite(value) && value > 0) {
        map.set(row.test_code, value);
      }
    });
    return map;
  }, [allTargets, tankId]);
  const chartSeries = useMemo(() => {
    const globalMaxDate = byTestWithData.reduce((maxDate, entry) => {
      if (!entry.data.length) return maxDate;
      return Math.max(maxDate, entry.data[entry.data.length - 1].date);
    }, 0);

    return byTestWithData.map((entry) => {
      const target = targetByCode.get(entry.def.code);
      const baseData =
        scaleMode === "relative"
          ? entry.data
              .map((point) => {
                if (!Number.isFinite(target) || target <= 0) return null;
                return {
                  date: point.date,
                  value: (point.value / target) * 100,
                  rawValue: point.value,
                  unit: entry.def.primaryUnit || "",
                };
              })
              .filter(Boolean)
          : entry.data.map((point) => ({
              date: point.date,
              value: point.value,
              rawValue: point.value,
              unit: entry.def.primaryUnit || "",
            }));
      const data = [...baseData];

      // Carry-forward: keep the latest reading visible until the latest chart date.
      if (data.length > 0 && globalMaxDate > data[data.length - 1].date) {
        const last = data[data.length - 1];
        data.push({
          ...last,
          date: globalMaxDate,
          carriedForward: true,
        });
      }

      return { ...entry, target, data };
    });
  }, [byTestWithData, targetByCode, scaleMode]);
  const hasChartData = useMemo(
    () => chartSeries.some((entry) => entry.data.length > 0),
    [chartSeries],
  );

  useEffect(() => {
    setHiddenCodes((prev) =>
      prev.filter((code) =>
        byTestWithData.some((entry) => entry.def.code === code),
      ),
    );
  }, [byTestWithData]);

  const selectedTankName = useMemo(
    () => tanks.find((tank) => tank.id === tankId)?.name || "",
    [tanks, tankId],
  );
  const tankCardRows = useMemo(() => {
    const cards = [
      ...tanks.map((tank) => ({ kind: "tank", key: tank.id, tank })),
      { kind: "add", key: "__add_tank__" },
    ];
    const rows = [];
    for (let i = 0; i < cards.length; i += 3) rows.push(cards.slice(i, i + 3));
    return rows;
  }, [tanks]);
  const latestByTankAndTest = useMemo(() => {
    const map = new Map();
    const asOfTs = (() => {
      const ts = new Date(`${dataAsOfDate}T23:59:59`).getTime();
      return Number.isFinite(ts) ? ts : Date.now();
    })();
    const grouped = new Map();
    rows.forEach((row) => {
      const rowTs = parseAppDateTime(row.recorded_at)?.getTime() ?? NaN;
      if (!Number.isFinite(rowTs)) return;
      const key = `${row.tank_id}::${row.test_code}`;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key).push({ row, ts: rowTs });
    });

    grouped.forEach((items, key) => {
      items.sort((a, b) => a.ts - b.ts);
      let previousOrExact = null;
      let nextAfter = null;
      for (let i = 0; i < items.length; i += 1) {
        const item = items[i];
        if (item.ts <= asOfTs) {
          previousOrExact = item;
        } else {
          nextAfter = item;
          break;
        }
      }
      const chosen = previousOrExact || nextAfter;
      if (!chosen) return;
      map.set(key, {
        ...chosen.row,
        _matchType: previousOrExact ? "previous" : "next",
      });
    });
    return map;
  }, [rows, dataAsOfDate]);
  const availableDataDates = useMemo(() => {
    const set = new Set();
    rows.forEach((row) => {
      if (!row?.recorded_at) return;
      const d = parseAppDateTime(row.recorded_at);
      if (!d) return;
      if (Number.isNaN(d.getTime())) return;
      const iso = d.toISOString().slice(0, 10);
      set.add(iso);
    });
    return Array.from(set).sort();
  }, [rows]);
  useEffect(() => {
    if (availableDataDates.length === 0) return;
    if (availableDataDates.includes(dataAsOfDate)) return;
    const currentTs = new Date(`${dataAsOfDate}T00:00:00`).getTime();
    let nearest = availableDataDates[0];
    let nearestDiff = Number.POSITIVE_INFINITY;
    availableDataDates.forEach((dateStr) => {
      const ts = new Date(`${dateStr}T00:00:00`).getTime();
      const diff = Math.abs(ts - currentTs);
      if (diff < nearestDiff) {
        nearest = dateStr;
        nearestDiff = diff;
      }
    });
    setDataAsOfDate(nearest);
  }, [availableDataDates, dataAsOfDate]);
  const stepDataDate = useCallback(
    (direction) => {
      if (availableDataDates.length === 0) return;
      const idx = availableDataDates.indexOf(dataAsOfDate);
      const baseIdx = idx >= 0 ? idx : 0;
      const nextIdx = Math.max(
        0,
        Math.min(availableDataDates.length - 1, baseIdx + direction),
      );
      setDataAsOfDate(availableDataDates[nextIdx]);
    },
    [availableDataDates, dataAsOfDate],
  );
  const latestDataRows = useMemo(() => {
    return tanks.map((tank) => {
      const byCode = {};
      SALIFERT_TEST_DEFINITIONS.forEach((test) => {
        const latest = latestByTankAndTest.get(`${tank.id}::${test.code}`);
        byCode[test.code] = latest
          ? `${latest.primary_value} ${latest.primary_unit || test.primaryUnit || ""}`.trim()
          : "—";
      });
      return { tank, byCode };
    });
  }, [tanks, latestByTankAndTest]);
  const visibleDataTests = useMemo(() => {
    return SALIFERT_TEST_DEFINITIONS.filter((test) =>
      tanks.some((tank) => latestByTankAndTest.has(`${tank.id}::${test.code}`)),
    );
  }, [tanks, latestByTankAndTest]);
  const allTargetsByTankCode = useMemo(() => {
    const map = new Map();
    allTargets.forEach((row) => {
      const val = Number(row?.target_value);
      if (!row?.tank_id || !row?.test_code || !Number.isFinite(val)) return;
      map.set(`${row.tank_id}::${row.test_code}`, val);
    });
    return map;
  }, [allTargets]);
  const evaluateStatus = useCallback(
    (value, target, code) => {
      if (!Number.isFinite(value)) {
        return { label: "No data", tone: "neutral", bg: withAlpha(theme.palette.text.disabled, 0.08) };
      }
      if (!Number.isFinite(target)) {
        return { label: "No target", tone: "neutral", bg: withAlpha(theme.palette.text.disabled, 0.08) };
      }
      const band = STATUS_BANDS[code] || { warn: Math.max(1, Math.abs(target) * 0.1), alert: Math.max(2, Math.abs(target) * 0.2) };
      const delta = value - target;
      const absDelta = Math.abs(delta);

      if (target <= 0) {
        if (value <= band.warn) {
          return { label: "OK", tone: "ok", bg: withAlpha(theme.palette.success.main, 0.14) };
        }
        if (value <= band.alert) {
          return { label: "High", tone: "warn", bg: withAlpha(theme.palette.warning.main, 0.16) };
        }
        return { label: "Very high", tone: "danger", bg: withAlpha(theme.palette.error.main, 0.16) };
      }
      if (absDelta <= band.warn) {
        return { label: "OK", tone: "ok", bg: withAlpha(theme.palette.success.main, 0.14) };
      }
      if (absDelta <= band.alert) {
        return {
          label: delta < 0 ? "Low" : "High",
          tone: "warn",
          bg: withAlpha(delta < 0 ? theme.palette.info.main : theme.palette.warning.main, 0.16),
        };
      }
      return {
        label: delta < 0 ? "Very low" : "Very high",
        tone: "danger",
        bg: withAlpha(theme.palette.error.main, 0.16),
      };
    },
    [theme],
  );

  const toggleGroupSx = {
    "& .MuiToggleButton-root": {
      flex: 1,
      px: { xs: 0.5, sm: 1 },
      whiteSpace: "nowrap",
    },
  };

  const mobileRowToggleSx = {
    flex: 1,
    minWidth: 0,
    "& .MuiToggleButton-root": {
      flex: 1,
      px: 0.5,
      py: 0.55,
      fontSize: "0.8125rem",
      lineHeight: 1.2,
      whiteSpace: "nowrap",
    },
  };

  const dataDateStepButtonSx = {
    alignSelf: "stretch",
    aspectRatio: "1 / 1",
    minWidth: "unset",
    minHeight: 0,
    width: "auto",
    borderRadius: "999px",
    px: 0,
    py: 0,
    flexShrink: 0,
    "& .anticon": { fontSize: 14 },
  };

  const dataDateControls = (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="stretch"
      sx={{
        flex: downMD ? 1 : undefined,
        minWidth: 0,
        width: downMD ? undefined : "auto",
      }}
    >
      <Button
        size="small"
        variant="outlined"
        onClick={() => stepDataDate(-1)}
        disabled={availableDataDates.length === 0 || dataAsOfDate === availableDataDates[0]}
        sx={dataDateStepButtonSx}
      >
        <LeftOutlined />
      </Button>
      <TextField
        size="small"
        type="date"
        value={dataAsOfDate}
        onChange={(e) => {
          const next = e.target.value;
          if (!next) return;
          if (availableDataDates.includes(next)) {
            setDataAsOfDate(next);
          }
        }}
        sx={{
          flex: 1,
          minWidth: 0,
          maxWidth: downMD ? "none" : 180,
          "& .MuiInputBase-input": { py: 0.85 },
        }}
        inputProps={{
          min: availableDataDates[0] || undefined,
          max: availableDataDates[availableDataDates.length - 1] || undefined,
        }}
      />
      <Button
        size="small"
        variant="outlined"
        onClick={() => stepDataDate(1)}
        disabled={
          availableDataDates.length === 0 ||
          dataAsOfDate === availableDataDates[availableDataDates.length - 1]
        }
        sx={dataDateStepButtonSx}
      >
        <RightOutlined />
      </Button>
    </Stack>
  );

  const tankSelect = viewMode === "graph" ? (
    <FormControl
      size="small"
      fullWidth={downMD}
      sx={{ minWidth: downMD ? 0 : 200, maxWidth: downMD ? "none" : 320 }}
    >
      <InputLabel id="trends-tank-select-label">Tank</InputLabel>
      <Select
        labelId="trends-tank-select-label"
        id="trends-tank-select"
        label="Tank"
        value={tankSelectValue}
        displayEmpty
        onChange={(e) => setSelectedTankId(e.target.value)}
      >
        <MenuItem value="">
          <em>Select tank</em>
        </MenuItem>
        {tanks.map((tank) => (
          <MenuItem key={tank.id} value={tank.id}>
            {tank.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  ) : null;

  const rangeToggle = viewMode === "graph" ? (
    <ToggleButtonGroup
      size="small"
      fullWidth={downMD}
      value={range}
      exclusive
      onChange={(_, next) => next && setRange(next)}
      sx={downMD ? toggleGroupSx : undefined}
    >
      <ToggleButton value="month">Month</ToggleButton>
      <ToggleButton value="week">Week</ToggleButton>
      <ToggleButton value="year">Year</ToggleButton>
      <ToggleButton value="all">All</ToggleButton>
    </ToggleButtonGroup>
  ) : null;

  const scaleToggle = viewMode === "graph" ? (
    <ToggleButtonGroup
      size="small"
      fullWidth={downMD}
      value={scaleMode}
      exclusive
      onChange={(_, next) => next && setScaleMode(next)}
      sx={downMD ? toggleGroupSx : undefined}
    >
      <ToggleButton value="relative">Relative</ToggleButton>
      <ToggleButton value="absolute">Absolute</ToggleButton>
    </ToggleButtonGroup>
  ) : null;

  const viewModeToggle = (
    <ToggleButtonGroup
      size="small"
      fullWidth={downMD}
      value={viewMode}
      exclusive
      onChange={(_, next) => next && setViewMode(next)}
      sx={{
        flexShrink: 0,
        ...(downMD ? toggleGroupSx : {}),
      }}
    >
      <ToggleButton value="data">{downMD ? "Data" : "Data view"}</ToggleButton>
      <ToggleButton value="graph">{downMD ? "Graph" : "Graph view"}</ToggleButton>
    </ToggleButtonGroup>
  );

  const desktopSecondary = (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ width: "100%", gap: 1 }}
    >
      {viewModeToggle}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        sx={{ flex: 1, minWidth: 0 }}
      >
        {viewMode === "data" ? dataDateControls : null}
        {tankSelect}
        {rangeToggle}
        {scaleToggle}
      </Stack>
    </Stack>
  );

  const mobileGraphTankSelect =
    viewMode === "graph" ? (
      <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
        <InputLabel id="trends-tank-select-mobile-label" shrink>
          Tank
        </InputLabel>
        <Select
          labelId="trends-tank-select-mobile-label"
          id="trends-tank-select-mobile"
          label="Tank"
          value={tankSelectValue}
          displayEmpty
          onChange={(e) => setSelectedTankId(e.target.value)}
        >
          <MenuItem value="">
            <em>Select tank</em>
          </MenuItem>
          {tanks.map((tank) => (
            <MenuItem key={tank.id} value={tank.id}>
              {tank.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    ) : null;

  const mobileToolbar = (
    <Stack spacing={1} sx={{ width: "100%" }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <ToggleButtonGroup
          size="small"
          value={viewMode}
          exclusive
          onChange={(_, next) => next && setViewMode(next)}
          sx={{ flexShrink: 0 }}
        >
          <ToggleButton value="data" sx={{ px: 1.25, whiteSpace: "nowrap" }}>
            Data
          </ToggleButton>
          <ToggleButton value="graph" sx={{ px: 1.25, whiteSpace: "nowrap" }}>
            Graph
          </ToggleButton>
        </ToggleButtonGroup>
        {viewMode === "graph" ? mobileGraphTankSelect : dataDateControls}
      </Stack>
      {viewMode === "graph" ? (
        <Stack direction="row" spacing={1} alignItems="stretch">
          <ToggleButtonGroup
            size="small"
            value={range}
            exclusive
            onChange={(_, next) => next && setRange(next)}
            sx={mobileRowToggleSx}
          >
            <ToggleButton value="month">Month</ToggleButton>
            <ToggleButton value="week">Week</ToggleButton>
            <ToggleButton value="year">Year</ToggleButton>
            <ToggleButton value="all">All</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            size="small"
            value={scaleMode}
            exclusive
            onChange={(_, next) => next && setScaleMode(next)}
            sx={mobileRowToggleSx}
          >
            <ToggleButton value="relative">Relative</ToggleButton>
            <ToggleButton value="absolute">Absolute</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      ) : null}
    </Stack>
  );

  const showSecondaryControls = !(viewMode === "graph" && !tankId);

  const pageTitle = useMemo(() => {
    if (viewMode === "graph" && tankId) {
      const name = selectedTankName?.trim();
      if (name) return `Trends for ${name}`;
    }
    return "Trends";
  }, [viewMode, tankId, selectedTankName]);

  const pageLoading = !tanksLoaded || dataLoading;
  const blockingPageLoad = useBlockingPageLoad(pageLoading);

  return (
    <SalifertTestPageShell
      title={pageTitle}
      secondary={showSecondaryControls && !downMD ? desktopSecondary : null}
    >
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 1, md: 1.25 },
          flex: 1,
          minHeight: 0,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <PageLoadingBar active={pageLoading} sx={{ mx: downMD ? 0.5 : 0, flexShrink: 0 }} />
        {downMD ? (
          <Box sx={{ flexShrink: 0, mb: 1, px: { xs: 0.5, md: 0 } }}>{mobileToolbar}</Box>
        ) : null}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
        {blockingPageLoad && rows.length === 0 ? (
          <PanelLoadingState minHeight={downMD ? 200 : 280} sx={{ flex: 1 }} />
        ) : (
          <KeepMountedTabPanels>
            <KeepMountedTabPanel active={viewMode === "data"} name="data">
              {downMD ? (
            <Box
              data-app-scroll-region=""
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <SalifertTrendsDataMobileTable
                tanks={tanks}
                visibleTests={visibleDataTests}
                latestByTankAndTest={latestByTankAndTest}
                allTargetsByTankCode={allTargetsByTankCode}
                goldenStandardValues={GOLDEN_STANDARD_VALUES}
                legendSymbols={LEGEND_SYMBOLS}
                evaluateStatus={evaluateStatus}
              />
            </Box>
          ) : (
            <Box
              data-app-scroll-region=""
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: "auto",
                overscrollBehavior: "contain",
              }}
            >
              <TableContainer sx={tableFrameSx}>
                <Table size="small" stickyHeader sx={tableSx}>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          ...trendsDataHeaderCellSx,
                          whiteSpace: "nowrap",
                          minWidth: 180,
                        }}
                      >
                        Tests
                      </TableCell>
                      {tanks.map((tank) => (
                        <TableCell
                          key={tank.id}
                          sx={{ ...trendsDataHeaderCellSx, whiteSpace: "nowrap" }}
                          align="left"
                        >
                          {tank.name}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tanks.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2}>
                          <Typography variant="body2" color="text.secondary">
                            No tanks or test data available yet.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      visibleDataTests.map((test, index) => (
                        <TableRow key={test.code} hover>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            <Stack
                              direction="row"
                              spacing={0.75}
                              alignItems="baseline"
                            >
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 600, flexShrink: 0 }}
                              >
                                #{index + 1}
                              </Typography>
                              <Typography component="span" variant="subtitle2">
                                {test.title}
                              </Typography>
                            </Stack>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Standard:{" "}
                              {Number.isFinite(GOLDEN_STANDARD_VALUES[test.code])
                                ? `${GOLDEN_STANDARD_VALUES[test.code]} ${test.primaryUnit || ""}`
                                : "—"}
                            </Typography>
                          </TableCell>
                          {tanks.map((tank) => {
                            const latest = latestByTankAndTest.get(
                              `${tank.id}::${test.code}`,
                            );
                            const current = latest
                              ? Number(latest.primary_value)
                              : NaN;
                            const target = allTargetsByTankCode.get(
                              `${tank.id}::${test.code}`,
                            );
                            const desired = Number.isFinite(target)
                              ? target
                              : GOLDEN_STANDARD_VALUES[test.code];
                            const status = evaluateStatus(
                              current,
                              desired,
                              test.code,
                            );
                            return (
                              <TableCell
                                key={`${tank.id}-${test.code}`}
                                sx={{ minWidth: 150 }}
                              >
                                <Box
                                  sx={{
                                    borderRadius: 1,
                                    px: 1,
                                    py: 0.75,
                                    bgcolor: status.bg,
                                    border: 1,
                                    borderColor: strongBorderColor,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, lineHeight: 1.25 }}
                                  >
                                    {latest
                                      ? `${latest.primary_value} ${latest.primary_unit || test.primaryUnit || ""}`.trim()
                                      : "—"}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: "block", lineHeight: 1.2 }}
                                  >
                                    Added:{" "}
                                    {latest
                                      ? formatDateOnly(latest.recorded_at)
                                      : "—"}
                                    {latest?._matchType === "next"
                                      ? " (next)"
                                      : ""}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: "block", lineHeight: 1.2 }}
                                  >
                                    {Number.isFinite(target)
                                      ? "Target"
                                      : "Desired"}
                                    :{" "}
                                    {Number.isFinite(desired)
                                      ? `${desired} ${test.primaryUnit || ""}`
                                      : "—"}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{ display: "block", lineHeight: 1.2 }}
                                  >
                                    Status: {status.label}
                                  </Typography>
                                </Box>
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
              )}
            </KeepMountedTabPanel>
            <KeepMountedTabPanel active={viewMode === "graph"} name="graph">
        {!tankId ? (
          <Stack
            spacing={2}
            data-app-scroll-region={downMD ? "" : undefined}
            sx={{
              flex: 1,
              minHeight: 0,
              alignItems: "center",
              justifyContent: "flex-start",
              ...(downMD
                ? scrollRegionNestedSx(1)
                : {
                    overflow: "auto",
                    WebkitOverflowScrolling: "touch",
                    p: { xs: 1, sm: 2 },
                  }),
            }}
          >
            <Box sx={{ position: "relative", mb: 1 }}>
              <Box
                sx={{
                  position: "absolute",
                  inset: -12,
                  borderRadius: "50%",
                  bgcolor: (theme) =>
                    withAlpha(theme.palette.primary.main, 0.16),
                  filter: "blur(16px)",
                }}
              />
              <Paper
                variant="outlined"
                sx={{
                  position: "relative",
                  p: 2,
                  borderRadius: 3,
                  borderColor: panelBorderColor,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ExperimentOutlined
                  style={{
                    fontSize: 42,
                    color: "var(--mui-palette-primary-main)",
                  }}
                />
              </Paper>
            </Box>
            <Typography variant="h5" sx={{ textAlign: "center" }}>
              Ready to dive into the data?
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", maxWidth: 560 }}
            >
              Please select one of your reef tanks below to view detailed
              trends, water chemistry, and history.
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Would you like to switch to the data view instead?
              </Typography>
              <Button size="small" variant="outlined" onClick={() => setViewMode("data")}>
                Open data view
              </Button>
            </Stack>
            <Stack spacing={1.5} sx={{ width: "100%", maxWidth: 980, mt: 1 }}>
              {tankCardRows.map((row, rowIdx) => (
                <Stack
                  key={`tank-row-${rowIdx}`}
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  justifyContent="center"
                  sx={{ width: "100%" }}
                >
                  {row.map((card) => (
                    <Box
                      key={card.key}
                      sx={{
                        flex: {
                          xs: "1 1 100%",
                          sm:
                            row.length === 1
                              ? "0 1 360px"
                              : `1 1 ${100 / row.length}%`,
                        },
                        maxWidth: {
                          xs: "100%",
                          sm:
                            row.length === 1 ? "360px" : `${100 / row.length}%`,
                        },
                        minWidth: 0,
                      }}
                    >
                      {card.kind === "tank" ? (
                        <Paper
                          variant="outlined"
                          onClick={() => setSelectedTankId(card.tank.id)}
                          sx={{
                            p: 1.5,
                            borderRadius: 2.5,
                            cursor: "pointer",
                            transition: "all 160ms ease",
                            "&:hover": {
                              borderColor: "primary.main",
                              boxShadow: (theme) =>
                                `0 6px 18px ${withAlpha(theme.palette.primary.main, 0.2)}`,
                            },
                          }}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            spacing={1}
                          >
                            <Stack
                              direction="row"
                              spacing={1.25}
                              alignItems="center"
                              sx={{ minWidth: 0 }}
                            >
                              <Box
                                sx={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: 1.75,
                                  bgcolor: (theme) =>
                                    withAlpha(theme.palette.primary.main, 0.12),
                                  color: "primary.main",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                }}
                              >
                                <AppstoreOutlined style={{ fontSize: 18 }} />
                              </Box>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {card.tank.name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  View trends
                                </Typography>
                              </Box>
                            </Stack>
                            <RightOutlined
                              style={{
                                color: "var(--mui-palette-text-disabled)",
                              }}
                            />
                          </Stack>
                        </Paper>
                      ) : (
                        <Paper
                          variant="outlined"
                          onClick={() => navigate("/tanks")}
                          sx={{
                            p: 1.5,
                            borderRadius: 2.5,
                            cursor: "pointer",
                            borderStyle: "dashed",
                            transition: "all 160ms ease",
                            "&:hover": {
                              borderColor: "primary.main",
                              bgcolor: "action.hover",
                            },
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={1.25}
                            alignItems="center"
                          >
                            <Box
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: 1.75,
                                bgcolor: "action.hover",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              <PlusOutlined style={{ fontSize: 14 }} />
                            </Box>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                            >
                              Add new tank
                            </Typography>
                          </Stack>
                        </Paper>
                      )}
                    </Box>
                  ))}
                </Stack>
              ))}
            </Stack>
          </Stack>
        ) : !hasChartData ? (
          <Box
            data-app-scroll-region={downMD ? "" : undefined}
            sx={{
              flex: 1,
              minHeight: 0,
              ...(downMD ? scrollRegionNestedSx(1) : { overflow: "auto", p: 1 }),
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No visible trend data. Select one or more legend items, or add
              target values for Relative mode.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              height: "100%",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <SalifertTrendsMuiDashboard
              chartSeries={chartSeries}
              byTestWithData={byTestWithData}
              hiddenCodes={hiddenCodes}
              onHiddenCodesChange={setHiddenCodes}
              range={range}
              scaleMode={scaleMode}
              targetByCode={targetByCode}
              legendSymbols={LEGEND_SYMBOLS}
              downMD={downMD}
            />
          </Box>
        )}
            </KeepMountedTabPanel>
          </KeepMountedTabPanels>
        )}
        </Box>
      </Paper>
    </SalifertTestPageShell>
  );
}
